import { ComponentConfig, usePuck } from '@measured/puck';
import {
  MarginProps,
  marginFields,
} from '../../../page-builder/puck/reusable-props/margin';
import { getCurrencyWithSymbol } from '../../../page-builder/utils/formatPrice';
import { isZeroOrEmpty } from '../../../page-builder/utils/isZeroOrEmpty';
import { ReduxSelectorProps } from '../../services/constants';
import CustomTooltip from '../../../components/common/tooltip';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import {
  getBorderProps,
  getcurrentDeviceType,
  getMarginStyles,
  getPaddingStyles,
  viewPorts,
} from '../../utils/helper';
import CustomizePanel from '../order-details/customize-props';
import showComponent, {
  showComponentProps,
} from '../../puck/reusable-props/showComponent';
import { getIsEdit } from '../../utils/constants';
import { useState } from 'react';
import { QuillTextEditor } from '../../puck/reusable-props/quill-editor';
import { Switch, Tooltip } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import {
  paddingFields,
  paddingProps,
  PaddingProps,
} from '../../puck/reusable-props/padding';
import Border, {
  borderProps,
  IBorderValueProps,
} from '../../puck/reusable-props/Border';
import { backgroungFields } from '../../puck/reusable-props/backgroungFields';
import { ICalculatedFee } from '../../interface/checkoutInterface';

export type Props = {
  fontSize: string;
  fontWeight: string;
  lineHeight: number;
  showOnMobile: boolean;
  showOnTablet: boolean;
  showOnDesktop: boolean;
  textColor: string;
  placeholder?: string;
};

export type OrderDetailsProps = {
  Subtotal: Props;
  Shipping: Props;
  GrandTotal: Props;
  Discounts: Props;
  EstimatedAmount: Props;
  SalesTax: Props;
  CalculatedFees: Props;
  background: {
    color: string;
    isInherit: boolean;
  };
  showOrderAccordion: boolean;
  showTotalDivider: boolean;
  orderDetails: string;
  border: IBorderValueProps;
} & MarginProps &
  PaddingProps &
  showComponentProps;

export const OrderDetails = ({
  useAppSelector,
}: ReduxSelectorProps): ComponentConfig<OrderDetailsProps> => {
  return {
    label: (
      <CustomTooltip title="OrderDetails">
        <span>Order Details</span>
      </CustomTooltip>
    ) as React.ReactNode as string,
    fields: {
      ...showComponent,
      ...marginFields,
      ...paddingFields,
      ...backgroungFields,
      border: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <Border value={value} onChange={onChange} title="Border" />
        ),
      },

      Subtotal: {
        type: 'custom',
        label: 'Margin',
        render: ({ value, onChange }) => (
          <CustomizePanel title="SubTotal" value={value} onChange={onChange} />
        ),
      },
      Discounts: {
        type: 'custom',
        label: 'Margin',
        render: ({ value, onChange }) => (
          <CustomizePanel title="Discounts" value={value} onChange={onChange} />
        ),
      },
      Shipping: {
        type: 'custom',
        label: 'Margin',
        render: ({ value, onChange }) => (
          <CustomizePanel title="Shipping" value={value} onChange={onChange} />
        ),
      },
      EstimatedAmount: {
        type: 'custom',
        label: 'Margin',
        render: ({ value, onChange }) => (
          <CustomizePanel
            title="EstimatedAmount"
            value={value}
            onChange={onChange}
          />
        ),
      },
      SalesTax: {
        type: 'custom',
        label: 'Margin',
        render: ({ value, onChange }) => (
          <CustomizePanel title="Sales Tax" value={value} onChange={onChange} />
        ),
      },
      CalculatedFees: {
        type: 'custom',
        label: 'Margin',
        render: ({ value, onChange }) => (
          <CustomizePanel
            title="Calculated Fees"
            value={value}
            onChange={onChange}
            isPlaceHolderNeed={false}
          />
        ),
      },
      GrandTotal: {
        type: 'custom',
        label: 'Margin',
        render: ({ value, onChange }) => (
          <CustomizePanel
            title="Grand Total"
            value={value}
            onChange={onChange}
          />
        ),
      },
      orderDetails: {
        ...QuillTextEditor('Customize Order Accordion').content,
      },
      showOrderAccordion: {
        type: 'custom',
        render: ({ onChange, value }) => {
          return (
            <>
              <div className="flex justify-between items-center mb-3">
                <p className="text-sm font-semibold">Show Accordion</p>
                <div className="flex gap-0.5 items-center">
                  <Tooltip
                    title={
                      'Toggle this switch to show the text in accordion in the order details section.'
                    }
                    placement="top-start">
                    <InfoIcon fontSize="small" className="h-1 w-1" />
                  </Tooltip>
                  <Switch
                    onChange={e => {
                      onChange(e.target.checked);
                    }}
                    checked={value}
                    inputProps={{ 'aria-label': 'controlled' }}
                  />
                </div>
              </div>
            </>
          );
        },
      },
      showTotalDivider: {
        type: 'radio',
        label: 'Show Divider Line For Total',
        options: [
          { value: true, label: 'True' },
          { value: false, label: 'False' },
        ],
      },
    },
    defaultProps: {
      showComponent: ['desktop', 'tablet', 'mobile'],
      showTotalDivider: false,
      margin: {
        desktop: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
        tablet: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
        mobile: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
      },
      ...paddingProps,
      ...borderProps,
      Subtotal: {
        placeholder: 'Subtotal',
        textColor: '#4B5563',
        fontSize: 'text-sm',
        fontWeight: 'font-normal',
        lineHeight: 2,
        showOnDesktop: true,
        showOnTablet: true,
        showOnMobile: true,
      },
      Shipping: {
        placeholder: 'Shipping',
        textColor: '#4B5563',
        fontSize: 'text-sm',
        fontWeight: 'font-normal',
        lineHeight: 24,
        showOnDesktop: true,
        showOnTablet: true,
        showOnMobile: true,
      },
      EstimatedAmount: {
        placeholder: 'Estimated taxes',
        textColor: '#4B5563',
        fontSize: 'text-sm',
        fontWeight: 'font-normal',
        lineHeight: 24,
        showOnDesktop: true,
        showOnTablet: true,
        showOnMobile: true,
      },
      SalesTax: {
        placeholder: 'Sales Tax',
        textColor: '#4B5563',
        fontSize: 'text-sm',
        fontWeight: 'font-normal',
        lineHeight: 24,
        showOnDesktop: true,
        showOnTablet: true,
        showOnMobile: true,
      },
      CalculatedFees: {
        textColor: '#4B5563',
        fontSize: 'text-sm',
        fontWeight: 'font-normal',
        lineHeight: 24,
        showOnDesktop: true,
        showOnTablet: true,
        showOnMobile: true,
      },
      GrandTotal: {
        placeholder: 'Total',
        textColor: '#000000',
        fontSize: 'text-base',
        fontWeight: 'font-semibold',
        lineHeight: 24,
        showOnDesktop: true,
        showOnTablet: true,
        showOnMobile: true,
      },
      Discounts: {
        placeholder: 'Discount Total',
        textColor: '#4B5563',
        fontSize: 'text-sm',
        fontWeight: 'font-normal',
        lineHeight: 24,
        showOnDesktop: true,
        showOnTablet: true,
        showOnMobile: true,
      },
      background: {
        color: '#ffffff',
        isInherit: false,
      },
      orderDetails:
        '<h3 style="line-height:1.5;"><span style="font-size: 20px;">Show order summary</span></h3>',
      showOrderAccordion: true,
    },
    render: ({
      margin,
      border,
      padding,
      Subtotal,
      Shipping,
      GrandTotal,
      orderDetails,
      EstimatedAmount,
      SalesTax,
      CalculatedFees,
      showTotalDivider,
      background,
      Discounts,
      showComponent,
      editMode,
      showOrderAccordion,
    }) => {
      const currentWidth = useAppSelector(state => state.store.currentWidth);
      let width = currentWidth;
      const isEditPage = getIsEdit();
      if (isEditPage) {
        const { appState } = usePuck();
        width = appState?.ui?.viewports?.current?.width;
      }
      const currentDeviceType = getcurrentDeviceType(currentWidth, editMode);
      const { cartData } = useAppSelector(state => state.cart);
      const selectedShippingMethod = useAppSelector(
        state => state.checkout.selectedShippingMethod,
      );

      const isFree = !(
        cartData &&
        !!cartData?.ShippingRequired &&
        cartData?.LineItems?.length > 0 &&
        selectedShippingMethod?.Price &&
        !['0.00', '0', '0.0'].includes(selectedShippingMethod?.Price) &&
        cartData?.Fees?.shipping?.originalCost &&
        !['0.00', '0', '0.0'].includes(cartData?.Fees?.shipping?.originalCost)
      );
      const marginStyles = getMarginStyles(margin, currentDeviceType);
      const [showDropdown, setshowDropdown] = useState(true);
      const toggleDropdown = () => setshowDropdown(!showDropdown);

      const calculatedFees = cartData?.Fees?.CalculatedFees || [];
      return (
        <>
          {showOrderAccordion && (
            <div className="flex justify-between text-editor-content">
              <button
                type="button"
                className="flex items-center w-full  justify-between pt-4 pb-4 "
                style={{
                  backgroundColor: background?.isInherit
                    ? 'inherit'
                    : background?.color,
                  lineHeight: 1.5,
                }}
                onClick={toggleDropdown}>
                <div dangerouslySetInnerHTML={{ __html: orderDetails }} />

                <ChevronDownIcon
                  className={`h-5 w-5 ml-2 transition-transform duration-200 ${showDropdown ? 'transform rotate-180' : ''}`}
                  aria-hidden="true"
                />
              </button>
            </div>
          )}
          {showComponent?.includes(currentDeviceType) && showDropdown && (
            <dl
              style={{
                backgroundColor: background?.isInherit
                  ? 'inherit'
                  : background?.color,
                ...marginStyles,
                ...getPaddingStyles(padding, currentDeviceType),
                ...getBorderProps(border, currentDeviceType),
              }}
              className={` text-sm font-medium text-gray-900 lg:block ${border?.borderStyle?.[currentDeviceType]}`}>
              {((Subtotal?.showOnMobile &&
                width &&
                width <= viewPorts.mobile) ||
                (Subtotal?.showOnTablet &&
                  width &&
                  width > viewPorts.mobile &&
                  width <= viewPorts.tablet) ||
                (Subtotal?.showOnDesktop &&
                  width &&
                  width > viewPorts.tablet)) && ( // Desktop: Width greater than tablet
                <div
                  className={`flex items-center justify-between ${Subtotal?.fontSize} ${Subtotal?.fontWeight}`}
                  style={{
                    color: Subtotal?.textColor,
                    paddingTop: Subtotal?.lineHeight + 'px',
                  }}>
                  <dt>{Subtotal.placeholder}</dt>
                  <dd>
                    {getCurrencyWithSymbol(
                      cartData?.currencyCode || 'USD',
                      String(cartData?.SubtotalAmount),
                    )}
                  </dd>
                </div>
              )}

              {((Discounts?.showOnMobile &&
                width &&
                width <= viewPorts.mobile) ||
                (Discounts?.showOnTablet &&
                  width &&
                  width > viewPorts.mobile &&
                  width <= viewPorts.tablet) ||
                (Discounts?.showOnDesktop &&
                  width &&
                  width > viewPorts?.tablet)) &&
                (isEditPage ? (
                  <div
                    className={`flex items-center justify-between ${Discounts?.fontSize} ${Discounts?.fontWeight}`}
                    style={{
                      color: Discounts?.textColor,
                      paddingTop: Discounts?.lineHeight + 'px',
                    }}>
                    <dt>{Discounts.placeholder}</dt>
                    {cartData?.DiscountAmount &&
                    !isZeroOrEmpty(cartData?.DiscountAmount) ? (
                      <dd>
                        -{' '}
                        {getCurrencyWithSymbol(
                          cartData?.currencyCode || 'USD',
                          cartData?.DiscountAmount,
                        )}
                      </dd>
                    ) : (
                      <dd> - </dd>
                    )}
                  </div>
                ) : (
                  <>
                    {cartData?.DiscountAmount &&
                      !isZeroOrEmpty(cartData?.DiscountAmount) && (
                        <div
                          className={`flex items-center justify-between ${Discounts?.fontSize} ${Discounts?.fontWeight}`}
                          style={{
                            color: Discounts?.textColor,
                            paddingTop: Discounts?.lineHeight + 'px',
                          }}>
                          <dt className="text-gray-700 font-normal text-sm">
                            {Discounts.placeholder}
                          </dt>
                          <dd>
                            -{' '}
                            {getCurrencyWithSymbol(
                              cartData?.currencyCode || 'USD',
                              cartData?.DiscountAmount || '',
                            )}
                          </dd>
                        </div>
                      )}
                  </>
                ))}

              {((Shipping?.showOnMobile &&
                width &&
                width <= viewPorts.mobile) ||
                (Shipping?.showOnTablet &&
                  width &&
                  width > viewPorts.mobile &&
                  width <= viewPorts.tablet) ||
                (Shipping?.showOnDesktop &&
                  width &&
                  width > viewPorts.tablet)) && ( // Desktop: Width greater than tablet
                <div
                  className={`flex items-center justify-between ${Shipping?.fontSize} ${Shipping?.fontWeight}`}
                  style={{
                    color: Shipping?.textColor,
                    paddingTop: Shipping?.lineHeight + 'px',
                  }}>
                  <dt>{Shipping.placeholder}</dt>
                  <div className="flex">
                    {cartData &&
                      !!cartData?.ShippingRequired &&
                      cartData?.LineItems?.length > 0 &&
                      selectedShippingMethod?.Price &&
                      !['0.00', '0', '0.0'].includes(
                        selectedShippingMethod?.Price,
                      ) &&
                      cartData?.Fees?.shipping?.originalCost &&
                      !['0.00', '0', '0.0'].includes(
                        cartData?.Fees?.shipping?.originalCost,
                      ) && (
                        <>
                          <dd
                            className={
                              cartData?.Fees?.shipping?.DiscountedCost
                                ? 'line-through text-gray-400 font-normal text-sm'
                                : ''
                            }>
                            {selectedShippingMethod?.Price
                              ? getCurrencyWithSymbol(
                                  cartData?.currencyCode,
                                  String(selectedShippingMethod?.Price),
                                )
                              : getCurrencyWithSymbol(
                                  cartData?.currencyCode || 'USD',
                                  String(
                                    cartData?.Fees?.shipping?.originalCost,
                                  ),
                                )}
                          </dd>
                          {cartData?.Fees?.shipping?.DiscountedCost && (
                            <dd className="ml-3 font-normal text-sm">
                              {getCurrencyWithSymbol(
                                cartData?.currencyCode || 'USD',
                                cartData?.Fees?.shipping?.DiscountedCost,
                              )}
                            </dd>
                          )}
                        </>
                      )}
                    {isFree && <dd>Free</dd>}
                  </div>
                </div>
              )}

              {((EstimatedAmount?.showOnMobile &&
                width &&
                width <= viewPorts.mobile) ||
                (EstimatedAmount?.showOnTablet &&
                  width &&
                  width > viewPorts.mobile &&
                  width <= viewPorts.tablet) ||
                (EstimatedAmount?.showOnDesktop &&
                  width &&
                  width > viewPorts.tablet)) &&
                (isEditPage ? (
                  <div
                    className={`flex items-center pb-4 justify-between ${EstimatedAmount?.fontSize} ${EstimatedAmount?.fontWeight}`}
                    style={{
                      color: EstimatedAmount?.textColor,
                      paddingTop: EstimatedAmount?.lineHeight + 'px',
                    }}>
                    <dt>{EstimatedAmount.placeholder}</dt>
                    {cartData?.EstimatedTaxAmount &&
                    Boolean(Number(cartData?.EstimatedTaxAmount)) ? (
                      <dd>
                        {getCurrencyWithSymbol(
                          cartData?.currencyCode || 'USD',
                          cartData?.EstimatedTaxAmount,
                        )}
                      </dd>
                    ) : (
                      <dd>-</dd>
                    )}
                  </div>
                ) : (
                  <>
                    {cartData?.EstimatedTaxAmount &&
                      Boolean(Number(cartData?.EstimatedTaxAmount)) && (
                        <div
                          className={`flex items-center pb-4 justify-between ${EstimatedAmount?.fontSize} ${EstimatedAmount?.fontWeight}`}
                          style={{
                            color: EstimatedAmount?.textColor,
                            paddingTop: EstimatedAmount?.lineHeight + 'px',
                          }}>
                          <dt>{EstimatedAmount.placeholder}</dt>
                          <dd>
                            {getCurrencyWithSymbol(
                              cartData?.currencyCode || 'USD',
                              cartData?.EstimatedTaxAmount,
                            )}
                          </dd>
                        </div>
                      )}
                  </>
                ))}

              {((SalesTax?.showOnMobile &&
                width &&
                width <= viewPorts.mobile) ||
                (SalesTax?.showOnTablet &&
                  width &&
                  width > viewPorts.mobile &&
                  width <= viewPorts.tablet) ||
                (SalesTax?.showOnDesktop &&
                  width &&
                  width > viewPorts.tablet)) &&
                (isEditPage ? (
                  <div
                    className={`flex items-center pb-4 justify-between ${SalesTax?.fontSize} ${SalesTax?.fontWeight}`}
                    style={{
                      color: SalesTax?.textColor,
                      paddingTop: SalesTax?.lineHeight + 'px',
                    }}>
                    <dt>{SalesTax.placeholder}</dt>
                    {cartData?.SalesTaxAmount &&
                    Boolean(Number(cartData?.SalesTaxAmount)) ? (
                      <dd>
                        {getCurrencyWithSymbol(
                          cartData?.currencyCode || 'USD',
                          cartData?.SalesTaxAmount,
                        )}
                      </dd>
                    ) : (
                      <dd>-</dd>
                    )}
                  </div>
                ) : (
                  <>
                    {cartData?.SalesTaxAmount &&
                      Boolean(Number(cartData?.SalesTaxAmount)) && (
                        <div
                          className={`flex items-center pb-4 justify-between ${SalesTax?.fontSize} ${SalesTax?.fontWeight}`}
                          style={{
                            color: SalesTax?.textColor,
                            paddingTop: SalesTax?.lineHeight + 'px',
                          }}>
                          <dt>{SalesTax.placeholder}</dt>
                          <dd>
                            {getCurrencyWithSymbol(
                              cartData?.currencyCode || 'USD',
                              cartData?.SalesTaxAmount,
                            )}
                          </dd>
                        </div>
                      )}
                  </>
                ))}

              {((CalculatedFees?.showOnMobile &&
                width &&
                width <= viewPorts.mobile) ||
                (CalculatedFees?.showOnTablet &&
                  width &&
                  width > viewPorts.mobile &&
                  width <= viewPorts.tablet) ||
                (CalculatedFees?.showOnDesktop &&
                  width &&
                  width > viewPorts.tablet)) &&
                (isEditPage ? (
                  <div
                    className={`flex items-center pb-4 justify-between ${CalculatedFees?.fontSize} ${CalculatedFees?.fontWeight}`}
                    style={{
                      color: CalculatedFees?.textColor,
                      paddingTop: CalculatedFees?.lineHeight + 'px',
                    }}>
                    <dt>Calculated Fee</dt>
                    {calculatedFees && calculatedFees.length > 0 ? (
                      <dd>
                        {getCurrencyWithSymbol(
                          cartData?.currencyCode || 'USD',
                          String(
                            calculatedFees.reduce(
                              (sum: number, fee: ICalculatedFee) =>
                                sum + Number(fee.ComputedAmount),
                              0,
                            ),
                          ),
                        )}
                      </dd>
                    ) : (
                      <dd>-</dd>
                    )}
                  </div>
                ) : (
                  <>
                    {calculatedFees &&
                      calculatedFees.map(
                        (fee: ICalculatedFee, index: number) => (
                          <div
                            key={index}
                            className={`flex items-center justify-between ${CalculatedFees?.fontSize} ${CalculatedFees?.fontWeight}`}
                            style={{
                              color: CalculatedFees?.textColor,
                              paddingTop: CalculatedFees?.lineHeight + 'px',
                            }}>
                            <dt>{fee.FeeName || CalculatedFees.placeholder}</dt>
                            <dd>
                              {getCurrencyWithSymbol(
                                cartData?.currencyCode || 'USD',
                                fee.ComputedAmount,
                              )}
                            </dd>
                          </div>
                        ),
                      )}
                  </>
                ))}

              {((GrandTotal?.showOnMobile &&
                width &&
                width <= viewPorts.mobile) ||
                (GrandTotal?.showOnTablet &&
                  width &&
                  width > viewPorts.mobile &&
                  width <= viewPorts.tablet) ||
                (GrandTotal?.showOnDesktop &&
                  width &&
                  width > viewPorts.tablet)) && (
                <div
                  className={`flex items-center justify-between ${GrandTotal.fontSize} ${GrandTotal.fontWeight} ${showTotalDivider ? 'border-t border-t-gray-200' : ''}`}
                  style={{
                    color: GrandTotal?.textColor,
                    paddingTop: GrandTotal?.lineHeight + 'px',
                  }}>
                  <dt>{GrandTotal.placeholder}</dt>
                  <dd>
                    {!cartData ||
                    !cartData.LineItems ||
                    cartData.LineItems.length === 0
                      ? '0.0'
                      : getCurrencyWithSymbol(
                          cartData?.currencyCode || 'USD',
                          cartData?.TotalAmount,
                        )}
                  </dd>
                </div>
              )}
            </dl>
          )}
        </>
      );
    },
  };
};
